import { useState } from 'react';
import { useEngagementsNonPayes, useEngagementsPayes } from '@/api/hooks';
import { BoutonExcel, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { OngletsTresorerie, StatutDossierBadge, TableDossiers, aujourdhui } from '@/components/tresorerie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, montant } from '@/lib/utils';

const debutMois = () => `${aujourdhui().slice(0, 8)}01`;

/** Rapports de trésorerie (§ 12) : engagements payés sur une période, engagements validés non payés par ancienneté. */
export function RapportsTresorerie() {
  const [depuis, setDepuis] = useState(debutMois());
  const [jusqua, setJusqua] = useState(aujourdhui());
  const payes = useEngagementsPayes({ depuis, jusqua });
  const nonPayes = useEngagementsNonPayes();
  const totalPayes = (payes.data ?? []).reduce((s, p) => s + p.montant, 0);
  return (
    <div>
      <PageHeader titre="Rapports de trésorerie" sousTitre="Engagements payés et engagements validés non encore payés." />
      <OngletsTresorerie />
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle>Engagements validés non payés</CardTitle>
                <CardDescription>Tous les FED autorisés encore en trésorerie, par ancienneté depuis leur validation.</CardDescription>
              </div>
              <BoutonExcel chemin="/tresorerie/rapports/engagements-non-payes.xlsx" nom={`tresorerie-engagements-non-payes-${aujourdhui()}.xlsx`} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {nonPayes.isLoading ? (
              <Chargement />
            ) : nonPayes.error || !nonPayes.data ? (
              <ErreurBloc erreur={nonPayes.error} />
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Kpi label="Total non payé" valeur={montant(nonPayes.data.montant)} detail={`${nonPayes.data.total} FED`} />
                  <Kpi label="0 à 7 jours" valeur={montant(nonPayes.data.anciennete['0-7'].montant)} detail={`${nonPayes.data.anciennete['0-7'].nombre} FED`} />
                  <Kpi label="8 à 30 jours" valeur={montant(nonPayes.data.anciennete['8-30'].montant)} detail={`${nonPayes.data.anciennete['8-30'].nombre} FED`} accent={nonPayes.data.anciennete['8-30'].nombre > 0 ? 'alerte' : undefined} />
                  <Kpi label="Plus de 30 jours" valeur={montant(nonPayes.data.anciennete['31+'].montant)} detail={`${nonPayes.data.anciennete['31+'].nombre} FED`} accent={nonPayes.data.anciennete['31+'].nombre > 0 ? 'critique' : undefined} />
                </div>
                <TableDossiers dossiers={nonPayes.data.dossiers} vide="Aucun engagement en attente de paiement." colonnes={['statut', 'semaine', 'mode', 'delai']} />
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle>Engagements payés</CardTitle>
                <CardDescription>Paiements confirmés sur la période, par compte et mode de paiement.</CardDescription>
              </div>
              <BoutonExcel chemin={`/tresorerie/rapports/engagements-payes.xlsx?depuis=${depuis}&jusqua=${jusqua}`} nom={`tresorerie-engagements-payes-${depuis}-${jusqua}.xlsx`} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="depuis">Du</Label>
                <Input id="depuis" type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="jusqua">Au</Label>
                <Input id="jusqua" type="date" value={jusqua} onChange={(e) => setJusqua(e.target.value)} />
              </div>
              <div className="pb-2 text-sm text-muted-foreground">
                {payes.data ? `${payes.data.length} paiement(s) · ${montant(totalPayes, true)}` : ''}
              </div>
            </div>
            {payes.isLoading ? (
              <Chargement />
            ) : payes.error || !payes.data ? (
              <ErreurBloc erreur={payes.error} />
            ) : payes.data.length === 0 ? (
              <Vide message="Aucun paiement sur la période." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payé le</TableHead>
                    <TableHead>FED</TableHead>
                    <TableHead>Objet · bénéficiaire</TableHead>
                    <TableHead className="hidden md:table-cell">Compte · mode</TableHead>
                    <TableHead className="hidden lg:table-cell">Référence</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payes.data.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{dateFr(p.datePaiement)}</TableCell>
                      <TableCell className="font-medium">
                        {p.numero}
                        <div className="text-xs text-muted-foreground">{p.structureCode}</div>
                      </TableCell>
                      <TableCell className="max-w-[34ch]">
                        <div className="truncate">{p.objet}</div>
                        <div className="truncate text-xs text-muted-foreground">{p.beneficiaire}</div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {p.compte ?? '—'}
                        <div className="text-xs text-muted-foreground">{p.mode === 'ESPECES' ? 'espèces' : 'virement'}</div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs">{p.reference ?? '—'}</TableCell>
                      <TableCell className="num text-right whitespace-nowrap">{montant(p.montant, true)}</TableCell>
                      <TableCell>
                        <StatutDossierBadge statut={p.statut} court />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
